import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDoubleDown, X } from "react-bootstrap-icons";
import XpressTermsContent from "./XpressTermsContent";
import styles from "./style.module.scss";

const SCROLL_THRESHOLD = 16;

const XpressTermsAndConditionsModal = ({ isOpen, onClose, onAccept }) => {
  const scrollRef = useRef(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const updateScrollState = () => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    const scrollableDistance = element.scrollHeight - element.clientHeight;
    const progress =
      scrollableDistance > 0
        ? Math.min((element.scrollTop / scrollableDistance) * 100, 100)
        : 100;

    setScrollProgress(progress);

    const atBottom =
      scrollableDistance <= SCROLL_THRESHOLD ||
      element.scrollHeight - element.scrollTop - element.clientHeight <=
        SCROLL_THRESHOLD;

    setHasScrolledToBottom(atBottom);
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    setHasScrolledToBottom(false);
    setScrollProgress(0);
    document.body.classList.add("modalOpen");

    const timer = setTimeout(updateScrollState, 0);

    return () => {
      clearTimeout(timer);
      document.body.classList.remove("modalOpen");
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleAccept = () => {
    if (!hasScrolledToBottom) {
      return;
    }

    onAccept();
    onClose();
  };

  const modalContent = (
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="xpress-terms-title"
      onClick={onClose}
    >
      <div
        className={styles.modalContainer}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <p className={styles.headerEyebrow}>Xpress Fleet Program</p>
            <h2 id="xpress-terms-title">Terms &amp; Conditions</h2>
            <p className={styles.headerSubtitle}>
              Please read the full agreement before accepting.
            </p>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close terms and conditions"
          >
            <X size={20} />
          </button>
        </div>

        <div className={styles.progressTrack} aria-hidden="true">
          <span
            className={styles.progressFill}
            style={{ width: `${scrollProgress}%` }}
          />
        </div>

        <div className={styles.modalBody}>
          <div
            ref={scrollRef}
            className={styles.modalBodyScroll}
            onScroll={updateScrollState}
          >
            <XpressTermsContent />
          </div>
          {!hasScrolledToBottom && <div className={styles.scrollFade} />}
        </div>

        <div className={styles.modalFooter}>
          {!hasScrolledToBottom && (
            <div className={styles.scrollHint}>
              <ChevronDoubleDown size={18} />
              <span>Scroll to the bottom to enable Accept</span>
            </div>
          )}
          <div className={styles.footerActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`${styles.acceptButton} ${
                hasScrolledToBottom ? styles.acceptButtonEnabled : ""
              }`}
              onClick={handleAccept}
              disabled={!hasScrolledToBottom}
            >
              Accept Terms &amp; Conditions
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default XpressTermsAndConditionsModal;
